/**
 * useBreakpoint.ts
 *
 * The single source of truth for viewport breakpoints. Values match Tailwind's
 * defaults, which this project does not override, so `useIsBelow('md')` and the
 * `md:` class prefix always agree. Do not introduce a component-local pixel
 * constant — that divergence is exactly what this module exists to prevent.
 *
 * Three exports, for three genuinely different needs:
 *
 *   useIsBelow    — live subscription. Re-renders when the viewport crosses the
 *                   boundary. Correct on the first render, which the hook it
 *                   replaces was not: that one initialised to `undefined` and
 *                   returned a falsy value, so every consumer rendered the desktop
 *                   tree once and then swapped. Harmless when one sidebar did it;
 *                   a visible flash of a full table on every list page once the
 *                   shared table branches on it.
 *   isBelowNow    — one-shot read, no subscription. For a component that must
 *                   *not* react to a resize: an editor canvas that would lose
 *                   unsaved work if an orientation change unmounted it.
 *   useIsTouchBand — live subscription to the *touch band*: narrow enough to be a
 *                   handheld AND driven by a finger. Two axes, not one. Use this
 *                   for behaviour that should follow the device rather than the
 *                   window — a focus owner, a keyboard-tracking listener, a
 *                   popover that must not close on a scroll it caused itself.
 *   isDesktopLayoutForced — the kill switch, below.
 *
 * Prefer a CSS `max-md:` / `md:` class over any of these when the difference is
 * purely visual. Reach for a JS branch only when the DOM must genuinely differ —
 * a different component, a different fetch, or a different focus owner.
 */
import { useSyncExternalStore } from 'react';

/** Tailwind's default breakpoints. Not overridden in tailwind.config.ts. */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** `'never'` opts a consumer out entirely — the query can never match. */
export type BreakpointOrNever = Breakpoint | 'never';

// ─── Kill switch ──────────────────────────────────────────────────────────────

/**
 * Forces every consumer of this module onto its desktop branch, so a bad
 * responsive change can be neutralised by configuration instead of a redeploy.
 * This is the only fast mitigation available: a code revert has to rebuild and
 * re-sync the bundle, and even then it only fixes *new* page loads — an open tab
 * keeps running the broken build until it is reloaded.
 *
 * Set either way:
 *   VITE_FORCE_DESKTOP_LAYOUT=true   at build time, for a deployed environment
 *   ?forceDesktopLayout=1            at runtime, for triaging one session
 *
 * Read once at module load on purpose. A live-reactive switch would let a URL
 * change remount half the app mid-interaction, which is worse than the bug it is
 * meant to contain.
 */
function resolveDesktopOverride(): boolean {
  if (import.meta.env?.VITE_FORCE_DESKTOP_LAYOUT === 'true') return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('forceDesktopLayout');
  } catch {
    // A malformed search string must never take the layout system down with it.
    return false;
  }
}

const DESKTOP_OVERRIDE = resolveDesktopOverride();

/** Whether the kill switch is active. Exported so a notice can be surfaced. */
export function isDesktopLayoutForced(): boolean {
  return DESKTOP_OVERRIDE;
}

// ─── Query plumbing ───────────────────────────────────────────────────────────

function mediaQueryFor(bp: BreakpointOrNever): string {
  // A max-width query is inclusive, so subtract 1: below `md` means <= 767px,
  // matching what Tailwind's own `max-md:` variant compiles to.
  return bp === 'never' ? '(max-width: 0px)' : `(max-width: ${BREAKPOINTS[bp] - 1}px)`;
}

/**
 * The touch band, as one string shared with CSS.
 *
 * Character-identical to the `@media` block at the bottom of `index.css`, and
 * `useBreakpoint.test.ts` asserts that against the stylesheet itself — the two are
 * a pair, and a boundary that must agree in two languages should be written once.
 *
 * Why two axes rather than `useIsBelow('lg')`:
 *
 *   - `pointer: coarse` is what separates a device from a window. An iPhone 14 in
 *     landscape is 844px and a Pro Max 932px, so a `md`-width gate misses them
 *     while the behaviour they need is fully required there. Conversely a mouse
 *     user who drags a desktop window narrow needs none of it, and a width-only
 *     query cannot tell the two apart.
 *   - `max-width: 1023.98px` keeps this incapable of matching a desktop viewport,
 *     which is what makes the frozen-desktop guarantee true by construction rather
 *     than by review.
 *   - `screen` excludes print, which lays out near 816px and would otherwise fall
 *     inside the bound.
 *
 * The bound is derived from `BREAKPOINTS.lg` rather than retyped, so moving the
 * breakpoint moves the band with it. `lg - 0.02` is Tailwind's own two-decimal
 * convention for an exclusive upper bound.
 */
export const TOUCH_BAND_QUERY =
  `screen and (max-width: ${BREAKPOINTS.lg - 0.02}px) and (pointer: coarse)` as const;

interface QueryEntry {
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => boolean;
}

/**
 * Entries are cached per query string, which matters for correctness and not just
 * allocation: useSyncExternalStore re-subscribes whenever the `subscribe`
 * function's identity changes, and a hook closing over its own argument cannot
 * hoist that function to module scope. Without this cache every render would tear
 * down and re-add a listener.
 */
const entries = new Map<string, QueryEntry>();

/** Used when there is no DOM — the node-environment test suites. */
const INERT: QueryEntry = {
  subscribe: () => () => {},
  getSnapshot: () => false,
};

function entryFor(query: string): QueryEntry {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return INERT;
  }

  let entry = entries.get(query);
  if (!entry) {
    const mql = window.matchMedia(query);
    entry = {
      subscribe: (onChange) => {
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
      },
      // Returns a boolean primitive, never an object. An object return would make
      // React see a new value every call and warn that getSnapshot should be
      // cached, then loop.
      getSnapshot: () => mql.matches,
    };
    entries.set(query, entry);
  }
  return entry;
}

// ─── Public hooks ─────────────────────────────────────────────────────────────

/**
 * True while the viewport is narrower than the named breakpoint. Correct on the
 * first render, and re-renders when the boundary is crossed.
 *
 * No server-snapshot argument: this is a client-only app, and omitting it only
 * throws under server rendering, which never happens here.
 */
export function useIsBelow(bp: BreakpointOrNever): boolean {
  const { subscribe, getSnapshot } = entryFor(mediaQueryFor(bp));
  const isBelow = useSyncExternalStore(subscribe, getSnapshot);
  // The hook is always called, before the override is applied — a conditional
  // call would break hook ordering.
  return DESKTOP_OVERRIDE ? false : isBelow;
}

/**
 * One-shot read with no subscription. Call it inside a `useState` initialiser to
 * latch a decision at mount and hold it for the component's lifetime.
 */
export function isBelowNow(bp: BreakpointOrNever): boolean {
  if (DESKTOP_OVERRIDE) return false;
  return entryFor(mediaQueryFor(bp)).getSnapshot();
}

/**
 * True while the viewport is inside the touch band — see `TOUCH_BAND_QUERY`.
 * Correct on the first render, and re-renders when either axis crosses.
 *
 * Reach for this over `useIsBelow` when the branch exists because a *finger* is
 * driving the UI: iOS raising a keyboard, a tap that carries a few pixels of drag,
 * a 44px target. Keep `useIsBelow` for branches that are about available width.
 */
export function useIsTouchBand(): boolean {
  const { subscribe, getSnapshot } = entryFor(TOUCH_BAND_QUERY);
  const inBand = useSyncExternalStore(subscribe, getSnapshot);
  // Called unconditionally, before the override is applied — see `useIsBelow`.
  return DESKTOP_OVERRIDE ? false : inBand;
}

/** One-shot read of the touch band, with no subscription. Pairs with `isBelowNow`. */
export function isTouchBandNow(): boolean {
  if (DESKTOP_OVERRIDE) return false;
  return entryFor(TOUCH_BAND_QUERY).getSnapshot();
}
